import React, { useState, useEffect, useRef, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Slider from "@mui/material/Slider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BookmarkIcon from "@mui/icons-material/Bookmark";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import VetorizarDialog from "../VetorizarDialog";

// Folha do Auto Cutter A3 (mesma geometria dos jobs reais da gráfica).
const FOLHA_W = 320;
const FOLHA_H = 450;

const FORMAS = [
  { value: "redondo", label: "Redondo" },
  { value: "quadrado", label: "Quadrado" },
  { value: "retangular", label: "Retangular" },
  { value: "tag", label: "Tag (com furo)" },
  { value: "lacre", label: "Lacre (halteres)" },
  { value: "contorno", label: "Contorno da arte (die-cut)" },
];
const TEM_ALTURA = ["retangular", "tag", "lacre", "contorno"];

// Mesmos modos de encaixe do PowerClip do CorelDRAW.
const AJUSTES = [
  { value: "proporcional", label: "Ajustar proporcionalmente" },
  { value: "preencher", label: "Preencher proporcionalmente" },
  { value: "esticar", label: "Esticar para preencher" },
  { value: "centro", label: "Centralizar" },
];

// Fontes do motor de textos dos adesivos (backend embute o TTF real via
// pdf-lib — ver src/assets/fonts/CREDITS.md). Nome lógico igual ao usado em
// `TextoElemento.fonte` / gerarAdesivosPdf.ts::FONTES.
const FONTES_TEXTO = [
  { value: "opensans", label: "Padrão" },
  { value: "montserrat", label: "Montserrat" },
  { value: "bebas", label: "Bebas Neue" },
  { value: "pacifico", label: "Pacifico" },
  { value: "dancing", label: "Dancing Script" },
  { value: "lobster", label: "Lobster" },
  { value: "vibes", label: "Great Vibes" },
];
// Preview em tela: stack de fallback (as 6 fontes não são carregadas no
// browser aqui — só embutidas no PDF final — então a prévia usa uma família
// de sistema com "personalidade" parecida; o PDF gerado é que sai fiel).
const FONTE_PREVIEW_CSS = {
  opensans: "'Open Sans', sans-serif",
  montserrat: "'Open Sans', sans-serif",
  bebas: "'Arial Narrow', Impact, sans-serif",
  pacifico: "cursive",
  dancing: "cursive",
  lobster: "cursive",
  vibes: "cursive",
};

const emptyForm = {
  tipo: "redondo",
  larguraMm: 20,
  alturaMm: 20,
  raioCantoMm: 3,
  furoMm: 4,       // tag
  furoTopoMm: 8,   // tag
  tiraMm: 18,      // lacre
  espacoMm: 3,
  sangriaMm: 2,
  margemMarcasMm: 12,
  recorteA5: true,
  material: "papel",
  acabamento: "nenhum",
  // Ajuste de corte (compensa o offset sensor→faca da máquina no .plt cru).
  // Padrão da máquina da KR (Teneth A3), CONFIRMADO por corte de teste 14/07:
  // -1mm deixou o corte perfeito (-1,5 passou pra cima). Ajustável por pedido.
  bladeMm: 0.35,
  overcutMm: 1,
  corteOffsetXMm: 0,
  corteOffsetYMm: -1,
};

const AdesivoDialog = ({ open, onClose, adesivoId, onSaved, initialTicketId, initialContact }) => {
  const [savedId, setSavedId] = useState(adesivoId || null);
  const [descricao, setDescricao] = useState("");
  const [savedData, setSavedData] = useState(null); // registro salvo (status, arquivos, aprovação)
  // Vínculo ao ticket: prop direta OU o que já ficou salvo no registro (ao reabrir da lista).
  const ticketId = initialTicketId || savedData?.ticketId || null;
  const contatoVinculado = initialContact || savedData?.contact || null;
  const [loading, setLoading] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [enviandoProva, setEnviandoProva] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  // "Salvar como modelo" (templates de etiqueta — Fase 3): mini-dialog pedindo
  // o nome do modelo, só disponível quando o adesivo já está salvo (tem id).
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateNome, setTemplateNome] = useState("");
  const [salvandoTemplate, setSalvandoTemplate] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [opcoes, setOpcoes] = useState({ materiais: [], acabamentos: [] });
  const [arteFileName, setArteFileName] = useState(null);
  const [arteUrl, setArteUrl] = useState(null); // dataURL local p/ preview
  const [enviandoArte, setEnviandoArte] = useState(false);
  // Die-cut (contorno da arte): pontos traçados pelo backend (traceSilhueta) +
  // margem branca (offset de lâmina) configurável.
  const [contornoPontos, setContornoPontos] = useState(null);
  // true quando a arte foi trocada (ou removida) depois de já existir um
  // contorno traçado — usado só pra diferenciar a mensagem "trace pela
  // primeira vez" de "trace de novo, a arte mudou".
  const [contornoDesatualizado, setContornoDesatualizado] = useState(false);
  const [margemBrancaMm, setMargemBrancaMm] = useState(1.5);
  const [tracandoContorno, setTracandoContorno] = useState(false);
  // Vetorização assistida (Fase 2): dialog isolado que gera um SVG (potrace
  // posterizado) a partir da arte já enviada e, se aceito, substitui a arte.
  const [vetorizarOpen, setVetorizarOpen] = useState(false);
  const [calc, setCalc] = useState({ porFolha: 0, colunas: 0, linhas: 0, porFolhaGirado: 0, folhas: 0, total: 0, sobra: 0 });
  // Venda por folha: quantidade pedida pelo cliente + preço da folha.
  const [qtdPedida, setQtdPedida] = useState("");
  const [precoFolha, setPrecoFolha] = useState("");
  // Textos no adesivo: fixos e sequenciais (dado variável). Arrastáveis no preview.
  const [textos, setTextos] = useState([]);
  const [textoSel, setTextoSel] = useState(null); // índice do texto selecionado p/ arrastar
  // Transform da arte dentro do adesivo: modo de encaixe (PowerClip do Corel) +
  // escala fina + deslocamento (arrastar).
  const [ajuste, setAjuste] = useState("proporcional");
  const [escalaPct, setEscalaPct] = useState(100);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // mm
  // Dimensões reais (px) da arte enviada — para o preview "contain" bater com o backend.
  const [arteDim, setArteDim] = useState(null);

  const arrastando = useRef(null);
  const previewRef = useRef(null);
  // mm→px do quadro de amostra, atualizado a cada render p/ o arrasto usar a escala certa.
  const mmToPxRef = useRef(1);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setNum = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value === "" ? "" : Number(e.target.value) }));

  const larguraMm = Number(form.larguraMm) || 0;
  const alturaMm = TEM_ALTURA.includes(form.tipo) ? (Number(form.alturaMm) || 0) : larguraMm;

  // Recalcula quantos cabem sempre que a forma/medidas/folgas mudam.
  const recalcular = useCallback(async () => {
    if (larguraMm < 5) { setCalc({ porFolha: 0, colunas: 0, linhas: 0, porFolhaGirado: 0, folhas: 0, total: 0, sobra: 0 }); return; }
    try {
      const { data } = await api.post("/adesivos/calcular", {
        tipo: form.tipo, larguraMm, alturaMm,
        raioCantoMm: form.raioCantoMm, espacoMm: form.espacoMm, margemMarcasMm: form.margemMarcasMm,
        qtdPedida: Number(qtdPedida) || 0, precoFolha: Number(precoFolha) || 0,
        textos,
      });
      setCalc(data);
    } catch { setCalc({ porFolha: 0, colunas: 0, linhas: 0, porFolhaGirado: 0, folhas: 0, total: 0, sobra: 0 }); }
  }, [form.tipo, larguraMm, alturaMm, form.raioCantoMm, form.espacoMm, form.margemMarcasMm, qtdPedida, precoFolha, textos]);

  useEffect(() => {
    const t = setTimeout(recalcular, 250);
    return () => clearTimeout(t);
  }, [recalcular]);

  useEffect(() => {
    api.get("/adesivos/opcoes")
      .then(({ data }) => setOpcoes(data))
      .catch(() => {});
  }, []);

  const handleUploadArte = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setEnviandoArte(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/adesivos/arte", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Arte trocada: o contorno traçado (se houver) pertence à arte ANTERIOR
      // — invalida pra não deixar preview/payload dessincronizados (a silhueta
      // de uma arte não faz sentido pra outra).
      setContornoDesatualizado(contornoPontos !== null);
      setContornoPontos(null);
      setArteFileName(data.fileName);
      // Preview: imagem servida pelo backend (PDF já rasterizado) como blob.
      const resp = await api.get(`/adesivos/arte/${data.fileName}`, { responseType: "blob" });
      setArteUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(resp.data); });
      setArteDim(null); // recaptura no onLoad da nova imagem
      setEscalaPct(100);
      setOffset({ x: 0, y: 0 });
      toast.success("Arte enviada. Ajuste o enquadramento e gere os arquivos.");
    } catch (err) { toastError(err); }
    finally { setEnviandoArte(false); }
  };

  // Vetorização assistida (Fase 2): o VetorizarDialog já rasterizou o SVG
  // aceito em um novo PNG (backend, /adesivos/vetorizar/aceitar) — aqui só
  // trocamos a arte do formulário para esse novo arquivo, no MESMO padrão do
  // handleUploadArte (preview + reset de enquadramento + invalidação do
  // contorno da Fase 1, já que a silhueta traçada pertencia à arte anterior).
  const handleAceitarVetorizacao = async (fileName) => {
    try {
      setContornoDesatualizado(contornoPontos !== null);
      setContornoPontos(null);
      setArteFileName(fileName);
      const resp = await api.get(`/adesivos/arte/${fileName}`, { responseType: "blob" });
      setArteUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(resp.data); });
      setArteDim(null);
      setEscalaPct(100);
      setOffset({ x: 0, y: 0 });
    } catch (err) { toastError(err); }
  };

  // Die-cut: liga a arte já enviada ao traceSilhueta (backend) e guarda os
  // pontos do contorno + atualiza largura/altura do formulário com o bbox real.
  const handleTracarContorno = async () => {
    if (!arteFileName) return;
    setTracandoContorno(true);
    try {
      const { data } = await api.post("/adesivos/tracar-contorno", {
        arteFileName,
        larguraMm: form.larguraMm || 50,
        offsetLaminaMm: margemBrancaMm,
      });
      setContornoPontos(data);
      setContornoDesatualizado(false);
      setForm((f) => ({ ...f, larguraMm: data.larguraMm, alturaMm: data.alturaMm }));
      toast.success(`Contorno traçado: ${data.pontos.length} pontos.`);
    } catch (err) { toastError(err); }
    finally { setTracandoContorno(false); }
  };

  const corpoBody = () => ({
    descricao,
    tipo: form.tipo, larguraMm, alturaMm,
    raioCantoMm: form.raioCantoMm,
    furoMm: form.furoMm, furoTopoMm: form.furoTopoMm, tiraMm: form.tiraMm,
    espacoMm: form.espacoMm,
    sangriaMm: form.sangriaMm, margemMarcasMm: form.margemMarcasMm,
    recorteA5: form.recorteA5,
    material: form.material, acabamento: form.acabamento,
    bladeMm: form.bladeMm, overcutMm: form.overcutMm,
    corteOffsetXMm: form.corteOffsetXMm, corteOffsetYMm: form.corteOffsetYMm,
    arteFileName, ajuste, escalaPct, offsetXMm: offset.x, offsetYMm: offset.y,
    qtdPedida: Number(qtdPedida) || 0, precoFolha: Number(precoFolha) || 0,
    textos,
    ...(form.tipo === "contorno" ? { pontos: contornoPontos?.pontos || [] } : {}),
    ...(ticketId ? { ticketId } : {}),
    ...(contatoVinculado?.id ? { contactId: contatoVinculado.id } : {}),
  });

  // Aplica a config de um adesivo salvo aos estados do formulário.
  const aplicarConfig = (c = {}) => {
    setForm({
      tipo: c.tipo || "redondo",
      larguraMm: c.larguraMm ?? 20,
      alturaMm: c.alturaMm ?? 20,
      raioCantoMm: c.raioCantoMm ?? 3,
      furoMm: c.furoMm ?? 4,
      furoTopoMm: c.furoTopoMm ?? 8,
      tiraMm: c.tiraMm ?? 18,
      espacoMm: c.espacoMm ?? 3,
      sangriaMm: c.sangriaMm ?? 2,
      margemMarcasMm: c.margemMarcasMm ?? 12,
      recorteA5: c.recorteA5 !== false,
      material: c.material || "papel",
      acabamento: c.acabamento || "nenhum",
      bladeMm: c.bladeMm ?? 0.35,
      overcutMm: c.overcutMm ?? 1,
      corteOffsetXMm: c.corteOffsetXMm ?? 0,
      // Adesivos antigos (sem ajuste salvo) herdam o padrão da máquina (-1);
      // quem salvou valor explícito mantém.
      corteOffsetYMm: c.corteOffsetYMm ?? -1,
    });
    setAjuste(c.ajuste || "proporcional");
    setEscalaPct(c.escalaPct ?? 100);
    setOffset({ x: c.offsetXMm ?? 0, y: c.offsetYMm ?? 0 });
    setTextos(Array.isArray(c.textos) ? c.textos : []);
    setQtdPedida(c.qtdPedida ? String(c.qtdPedida) : "");
    setPrecoFolha(c.precoFolha ? String(c.precoFolha) : "");
    setArteFileName(c.arteFileName || null);
    // Die-cut salvo: restaura os pontos já traçados (evita re-traçar ao reabrir).
    setContornoPontos(
      c.tipo === "contorno" && Array.isArray(c.pontos) && c.pontos.length
        ? { pontos: c.pontos, larguraMm: c.larguraMm, alturaMm: c.alturaMm }
        : null
    );
    setContornoDesatualizado(false);
    setMargemBrancaMm(c.offsetLaminaMm ?? c.margemBrancaMm ?? 1.5);
  };

  const loadAdesivo = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/adesivos/${id}`);
      setSavedData(data);
      setDescricao(data.descricao || "");
      aplicarConfig(data.config || {});
      if (data.config?.arteFileName) {
        try {
          const resp = await api.get(`/adesivos/arte/${data.config.arteFileName}`, { responseType: "blob" });
          setArteUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(resp.data); });
          setArteDim(null);
        } catch { /* sem preview da arte */ }
      }
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSavedId(adesivoId || null);
    setSavedData(null);
    setArteUrl((old) => { if (old) URL.revokeObjectURL(old); return null; });
    if (adesivoId) loadAdesivo(adesivoId);
    else { restaurarPadrao(); setArteFileName(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adesivoId, loadAdesivo]);

  // Cria (se novo) ou atualiza, devolvendo o id.
  const ensureSaved = async () => {
    if (savedId) { await api.put(`/adesivos/${savedId}`, corpoBody()); return savedId; }
    const { data } = await api.post("/adesivos", corpoBody());
    setSavedId(data.id);
    return data.id;
  };

  const handleSalvar = async () => {
    setLoading(true);
    try { await ensureSaved(); toast.success("Rascunho salvo."); onSaved && onSaved(); }
    catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const handleGerar = async () => {
    if (!arteFileName && textos.length === 0) { toast.warn("Envie uma arte ou adicione um texto."); return; }
    setGerando(true);
    try {
      const id = await ensureSaved();
      const { data } = await api.post(`/adesivos/${id}/generate`, corpoBody());
      setSavedData(data);
      toast.success(`Gerado: ${data.folhas} folha(s).`);
      onSaved && onSaved();
    } catch (err) { toastError(err); }
    finally { setGerando(false); }
  };

  const baixarArquivo = async (tipo) => {
    if (!savedId) return;
    try {
      const { data } = await api.get(`/adesivos/${savedId}/${tipo}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (tipo === "conferencia") {
        // Conferência é para VER: abre em nova aba.
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = `${savedData?.numero || "adesivos"}.${tipo === "plt" ? "plt" : "pdf"}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) { toastError(err); }
  };

  const handleEnviarProva = async () => {
    if (!savedId || !ticketId) return;
    setEnviandoProva(true);
    try {
      await api.post(`/adesivos/${savedId}/send/${ticketId}`);
      setSavedData((d) => ({ ...(d || {}), status: "enviado", arteEnviadaEm: new Date().toISOString() }));
      toast.success("Prova enviada ao cliente. Aguarde a aprovação.");
      onSaved && onSaved();
    } catch (err) { toastError(err); }
    finally { setEnviandoProva(false); }
  };

  const handleAprovar = async () => {
    if (!savedId) return;
    setAprovando(true);
    try {
      const { data } = await api.post(`/adesivos/${savedId}/aprovar`, { aprovada: true });
      setSavedData(data);
      toast.success("Aprovação registrada.");
      onSaved && onSaved();
    } catch (err) { toastError(err); }
    finally { setAprovando(false); }
  };

  const abrirSalvarTemplate = () => {
    setTemplateNome(descricao || "");
    setTemplateDialogOpen(true);
  };

  const handleSalvarTemplate = async () => {
    if (!savedId) return;
    const nome = templateNome.trim();
    if (!nome) { toast.warn("Informe um nome para o modelo."); return; }
    setSalvandoTemplate(true);
    try {
      await api.post(`/adesivos/${savedId}/salvar-template`, { templateNome: nome });
      toast.success("Salvo como modelo.");
      setTemplateDialogOpen(false);
      onSaved && onSaved();
    } catch (err) { toastError(err); }
    finally { setSalvandoTemplate(false); }
  };

  const temArquivos = !!savedData?.arquivoPdfFileName;

  // ── Arrastar arte OU texto no preview ─────────────────────────────────────
  // Um texto tem data-texto=<i>; se o arrasto começou nele, move o texto; senão
  // move a arte. A captura fica sempre no quadro para não perder o movimento.
  const onPointerDown = (e) => {
    const tEl = e.target.closest?.("[data-texto]");
    if (tEl) {
      const i = Number(tEl.dataset.texto);
      setTextoSel(i);
      arrastando.current = { tipo: "texto", i, x: e.clientX, y: e.clientY,
        o: { x: textos[i].offsetXMm, y: textos[i].offsetYMm } };
    } else if (arteUrl) {
      arrastando.current = { tipo: "arte", x: e.clientX, y: e.clientY, o: { ...offset } };
    } else return;
    e.preventDefault(); // evita o gesto de arrastar rolar a página (a tela "subindo")
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!arrastando.current) return;
    e.preventDefault();
    const ppm = mmToPxRef.current;
    const dx = (e.clientX - arrastando.current.x) / ppm;
    const dy = (e.clientY - arrastando.current.y) / ppm;
    if (arrastando.current.tipo === "texto") {
      const i = arrastando.current.i;
      const nx = Math.round((arrastando.current.o.x + dx) * 10) / 10;
      const ny = Math.round((arrastando.current.o.y + dy) * 10) / 10;
      setTextos((ts) => ts.map((t, j) => (j === i ? { ...t, offsetXMm: nx, offsetYMm: ny } : t)));
    } else {
      setOffset({ x: arrastando.current.o.x + dx, y: arrastando.current.o.y + dy });
    }
  };
  const onPointerUp = () => { arrastando.current = null; };

  // Texto exibido no preview (fixo = o texto; sequencial = o primeiro número).
  const textoExemplo = (t) => {
    if (t.modo === "fixo") return t.texto || "texto";
    const zeros = Math.max(0, Number(t.zeros) || 0);
    const corpo = zeros > 0 ? String(t.inicial ?? 0).padStart(zeros, "0") : String(t.inicial ?? 0);
    return `${t.prefixo || ""}${corpo}${t.sufixo || ""}`;
  };

  const resetEnquadramento = () => { setEscalaPct(100); setOffset({ x: 0, y: 0 }); };

  // Trocar de modo recentraliza a arte (o novo modo é o ponto de partida).
  const trocarAjuste = (v) => { setAjuste(v); setEscalaPct(100); setOffset({ x: 0, y: 0 }); };

  // Restaura TODAS as configurações ao padrão (mantém a arte enviada).
  const restaurarPadrao = () => {
    setForm(emptyForm);
    setAjuste("proporcional");
    setEscalaPct(100);
    setOffset({ x: 0, y: 0 });
    setQtdPedida("");
    setPrecoFolha("");
    setTextos([]);
    setTextoSel(null);
    setContornoPontos(null);
    setContornoDesatualizado(false);
    setMargemBrancaMm(1.5);
  };

  // ── Textos no adesivo ──────────────────────────────────────────────────────
  const novoTexto = (modo, tipo = "texto") => setTextos((ts) => {
    const base = {
      tipo, simbologia: "code128",
      modo, texto: "", prefixo: "", sufixo: "",
      inicial: 1, final: 100, passo: 1, zeros: 0,
      tamanhoMm: tipo === "texto" ? 4 : tipo === "qrcode" ? 14 : 8,
      cor: "#000000", offsetXMm: 0, offsetYMm: 0, alinhamento: "center",
      fonte: "opensans",
    };
    setTextoSel(ts.length);
    return [...ts, base];
  });
  const updTexto = (i, campo, val) =>
    setTextos((ts) => ts.map((t, j) => (j === i ? { ...t, [campo]: val } : t)));
  const rmTexto = (i) => {
    setTextos((ts) => ts.filter((_, j) => j !== i));
    setTextoSel(null);
  };

  // Um adesivo de amostra no preview, com a arte posicionada como sairá.
  const amostraPx = 460; // lado do quadro de amostra (bem grande, p/ encaixar texto)
  const mmToPx = amostraPx / Math.max(larguraMm, alturaMm, 1);
  mmToPxRef.current = mmToPx;
  const adW = larguraMm * mmToPx;
  const adH = alturaMm * mmToPx;
  const raioAmostra =
    form.tipo === "redondo" ? "50%" :
    form.tipo === "lacre" ? `${(larguraMm / 2) * mmToPx}px` : // pill (aprox.; corte real na conferência)
    form.tipo === "retangular" || form.tipo === "quadrado" || form.tipo === "tag" ? `${form.raioCantoMm * mmToPx}px` : 0;

  // Tamanho da arte no preview, espelhando o MESMO encaixe do backend (os modos
  // do PowerClip). O tile é o adesivo + sangria; o slider é ajuste fino.
  let arteEstilo = null;
  if (arteDim) {
    const tileWmm = larguraMm + 2 * (Number(form.sangriaMm) || 0);
    const tileHmm = alturaMm + 2 * (Number(form.sangriaMm) || 0);
    const containMm = Math.min(tileWmm / arteDim.w, tileHmm / arteDim.h);
    const coverMm = Math.max(tileWmm / arteDim.w, tileHmm / arteDim.h);
    const fino = Math.max(escalaPct, 1) / 100;
    if (ajuste === "esticar") {
      arteEstilo = { width: tileWmm * fino * mmToPx, height: tileHmm * fino * mmToPx };
    } else {
      const baseMm =
        ajuste === "preencher" ? coverMm
        : ajuste === "centro" ? Math.min(containMm, 25.4 / 72) // não amplia além do natural
        : containMm;
      const escala = baseMm * fino;
      arteEstilo = { width: arteDim.w * escala * mmToPx, height: arteDim.h * escala * mmToPx };
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ContentCutIcon color="primary" />
          <span style={{ flex: 1 }}>
            {savedData?.numero ? `Adesivos ${savedData.numero}` : "Novo adesivo"} — folha + corte (Auto Cutter A3)
          </span>
          {savedId && (
            <Button size="small" startIcon={<BookmarkIcon />} onClick={abrirSalvarTemplate}>
              Salvar como modelo
            </Button>
          )}
          <Button size="small" startIcon={<RestartAltIcon />} onClick={restaurarPadrao}>
            Restaurar padrão
          </Button>
        </div>
      </DialogTitle>
      <DialogContent dividers sx={{ overscrollBehavior: "contain" }}>
      {ticketId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Vinculado ao atendimento{contatoVinculado?.name ? ` de ${contatoVinculado.name}` : ` #${ticketId}`}.
          Depois de gerar, use <b>Enviar prova</b> para mandar a imagem com marca d'água ao cliente.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField label="Descrição (cliente / pedido)" fullWidth size="small"
            value={descricao} onChange={(e) => setDescricao(e.target.value)}
            placeholder="Ex.: Ricardo — Exame" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Material</InputLabel>
            <Select value={form.material} label="Material" onChange={set("material")}>
              {opcoes.materiais.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Acabamento</InputLabel>
            <Select value={form.acabamento} label="Acabamento" onChange={set("acabamento")}>
              {opcoes.acabamentos.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Coluna esquerda: parâmetros */}
        <Grid item xs={12} md={5}>
          <Button component="label" variant="outlined" fullWidth
            disabled={enviandoArte}
            startIcon={enviandoArte ? <CircularProgress size={16} /> : <UploadFileIcon />}>
            {arteFileName ? "Trocar arte" : "Enviar arte (PNG, JPG ou PDF)"}
            <input type="file" accept="image/png,image/jpeg,application/pdf" hidden onChange={handleUploadArte} />
          </Button>

          {arteFileName && (
            <Button size="small" fullWidth sx={{ mt: 0.5 }}
              startIcon={<AutoFixHighIcon />}
              onClick={() => setVetorizarOpen(true)}>
              Vetorizar logo (converter foto/print em vetor, sem Corel)
            </Button>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Formato</InputLabel>
                <Select value={form.tipo} label="Formato" onChange={set("tipo")}>
                  {FORMAS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {form.tipo === "contorno" ? (
              <>
                <Grid item xs={6}>
                  <TextField label="Margem branca" type="number" fullWidth size="small"
                    helperText="offset de lâmina ao redor da arte"
                    value={margemBrancaMm}
                    onChange={(e) => setMargemBrancaMm(e.target.value === "" ? "" : Number(e.target.value))}
                    inputProps={{ min: 0, max: 5, step: 0.1 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                </Grid>
                <Grid item xs={6} sx={{ display: "flex", alignItems: "center" }}>
                  <Button size="small" variant="outlined" fullWidth
                    disabled={!arteFileName || tracandoContorno}
                    startIcon={tracandoContorno ? <CircularProgress size={16} /> : <ContentCutIcon />}
                    onClick={handleTracarContorno}>
                    Traçar contorno da arte
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  {contornoPontos ? (
                    <Alert severity="success" sx={{ py: 0 }}>
                      Contorno traçado: {contornoPontos.pontos.length} pontos
                      {" "}({Math.round(contornoPontos.larguraMm)}×{Math.round(contornoPontos.alturaMm)}mm).
                    </Alert>
                  ) : contornoDesatualizado ? (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      Arte trocada — trace o contorno novamente.
                    </Alert>
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      {arteFileName
                        ? "Clique em \"Traçar contorno da arte\" para gerar o corte pela silhueta."
                        : "Envie a arte primeiro para poder traçar o contorno."}
                    </Typography>
                  )}
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6}>
                  <TextField label={form.tipo === "redondo" ? "Diâmetro" : form.tipo === "lacre" ? "Largura (ponta)" : "Largura"} type="number"
                    fullWidth size="small" value={form.larguraMm} onChange={setNum("larguraMm")}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                </Grid>
                {TEM_ALTURA.includes(form.tipo) && (
                  <Grid item xs={6}>
                    <TextField label={form.tipo === "lacre" ? "Comprimento" : "Altura"} type="number" fullWidth size="small"
                      value={form.alturaMm} onChange={setNum("alturaMm")}
                      InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                  </Grid>
                )}
              </>
            )}
            {(form.tipo === "quadrado" || form.tipo === "retangular" || form.tipo === "tag") && (
              <Grid item xs={6}>
                <TextField label="Raio do canto" type="number" fullWidth size="small"
                  value={form.raioCantoMm} onChange={setNum("raioCantoMm")}
                  InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
              </Grid>
            )}
            {form.tipo === "tag" && (
              <>
                <Grid item xs={6}>
                  <TextField label="Furo (diâmetro)" type="number" fullWidth size="small"
                    helperText="0 = sem furo"
                    value={form.furoMm} onChange={setNum("furoMm")}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Furo: do topo" type="number" fullWidth size="small"
                    helperText="até o centro do furo"
                    value={form.furoTopoMm} onChange={setNum("furoTopoMm")}
                    InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
                </Grid>
              </>
            )}
            {form.tipo === "lacre" && (
              <Grid item xs={6}>
                <TextField label="Largura da tira" type="number" fullWidth size="small"
                  helperText="parte estreita do meio"
                  value={form.tiraMm} onChange={setNum("tiraMm")}
                  InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }}><Typography variant="caption">Folgas de produção</Typography></Divider>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Espaço" type="number" fullWidth size="small"
                helperText="entre cortes" value={form.espacoMm} onChange={setNum("espacoMm")}
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Sangria" type="number" fullWidth size="small"
                helperText="vaza a arte" value={form.sangriaMm} onChange={setNum("sangriaMm")}
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Margem" type="number" fullWidth size="small"
                helperText="das marcas" value={form.margemMarcasMm} onChange={setNum("margemMarcasMm")}
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox size="small" checked={!!form.recorteA5}
                  onChange={(e) => setForm((f) => ({ ...f, recorteA5: e.target.checked }))} />}
                label="Incluir marca de recorte para envelope A5 no PDF"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }}><Typography variant="caption">Ajuste de corte (calibração da máquina)</Typography></Divider>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Desloca corte ↔ (X)" type="number" fullWidth size="small"
                helperText="+ direita / − esquerda"
                value={form.corteOffsetXMm} onChange={setNum("corteOffsetXMm")}
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Desloca corte ↕ (Y)" type="number" fullWidth size="small"
                helperText="+ baixo / − cima"
                value={form.corteOffsetYMm} onChange={setNum("corteOffsetYMm")}
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Compensação de faca" type="number" fullWidth size="small"
                helperText="offset da lâmina (0,35 padrão)"
                value={form.bladeMm} onChange={setNum("bladeMm")}
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Overcut" type="number" fullWidth size="small"
                helperText="fecha o corte (1,0 padrão)"
                value={form.overcutMm} onChange={setNum("overcutMm")}
                InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Já vem com <b>−1mm</b> (padrão da Teneth A3 da KR, confirmado no corte).
                Se sair deslocado, ajuste aqui. Veja o resultado na <b>conferência</b>.
              </Typography>
            </Grid>
          </Grid>

          <Alert severity={calc.porFolha > 0 ? "success" : "warning"} sx={{ mt: 2 }}>
            {calc.porFolha > 0 ? (
              <>
                Cabem <b>{calc.porFolha} por folha</b> ({calc.colunas} × {calc.linhas}).
                {form.tipo === "retangular" && calc.porFolhaGirado > calc.porFolha && (
                  <> Girado 90° caberiam <b>{calc.porFolhaGirado}</b> — troque largura e altura para render mais.</>
                )}
              </>
            ) : (
              <>Não cabe nenhum adesivo com essas medidas. Reduza a peça ou as folgas.</>
            )}
          </Alert>

          {calc.porFolha > 0 && Number(form.espacoMm) < 3 && (
            <Alert severity="warning" sx={{ mt: 1 }} icon={false}>
              ⚠️ Espaço de {form.espacoMm}mm entre os adesivos é apertado — pode <b>dificultar a depilagem</b>
              (tirar a película). Aumente o espaço se puder; se precisar deste para render mais folhas, siga assim.
            </Alert>
          )}

          <Divider sx={{ my: 2 }}><Typography variant="caption">Venda por folha</Typography></Divider>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <TextField label="Cliente quer" type="number" fullWidth size="small"
                value={calc.qtdSequencial > 0 ? calc.qtdSequencial : qtdPedida}
                disabled={calc.qtdSequencial > 0}
                helperText={calc.qtdSequencial > 0 ? "definido pela faixa" : undefined}
                onChange={(e) => setQtdPedida(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">un</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Preço da folha" type="number" fullWidth size="small"
                value={precoFolha} onChange={(e) => setPrecoFolha(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              {calc.folhas > 0 ? (
                <Typography variant="body2">
                  <b>{calc.folhas} folha(s)</b>
                  {calc.total > 0 && <> · <b>R$ {calc.total.toFixed(2).replace(".", ",")}</b></>}
                </Typography>
              ) : (
                <Typography variant="caption" color="textSecondary">Informe a quantidade</Typography>
              )}
            </Grid>
            {calc.folhas > 0 && calc.sobra > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Sobram {calc.sobra} adesivo(s) na última folha ({calc.folhas} × {calc.porFolha} = {calc.folhas * calc.porFolha}).
                </Typography>
              </Grid>
            )}
          </Grid>

          {arteFileName && (
            <>
              <Divider sx={{ my: 2 }}><Typography variant="caption">Enquadramento da arte</Typography></Divider>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Ajuste (como no PowerClip do Corel)</InputLabel>
                <Select value={ajuste} label="Ajuste (como no PowerClip do Corel)"
                  onChange={(e) => trocarAjuste(e.target.value)}>
                  {AJUSTES.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary">
                Escala fina — 100% = o ajuste acima puro · menos encolhe · mais amplia
              </Typography>
              <Slider size="small" value={escalaPct} min={20} max={400} step={1}
                onChange={(_, v) => setEscalaPct(v)}
                marks={[{ value: 100, label: "100%" }]}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`} />
              <Button size="small" startIcon={<RestartAltIcon />} onClick={resetEnquadramento}>
                Centralizar
              </Button>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                Arraste a arte no quadro à direita para reposicionar.
              </Typography>
            </>
          )}

          {/* ── Textos no adesivo (fixo + sequencial) ── */}
          <Divider sx={{ my: 2 }}><Typography variant="caption">Textos no adesivo</Typography></Divider>
          {textos.map((t, i) => (
            <Paper key={i} variant="outlined"
              onClick={() => setTextoSel(i)}
              sx={{ p: 1, mb: 1, borderColor: textoSel === i ? "primary.main" : undefined, cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select value={t.tipo || "texto"} onChange={(e) => updTexto(i, "tipo", e.target.value)}>
                    <MenuItem value="texto">Texto</MenuItem>
                    <MenuItem value="qrcode">QR Code</MenuItem>
                    <MenuItem value="barcode">Cód. barras</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select value={t.modo} onChange={(e) => updTexto(i, "modo", e.target.value)}>
                    <MenuItem value="fixo">{(t.tipo || "texto") === "texto" ? "Fixo" : "Valor fixo"}</MenuItem>
                    <MenuItem value="sequencial">Sequencial</MenuItem>
                  </Select>
                </FormControl>
                {t.tipo === "barcode" && (
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select value={t.simbologia || "code128"} onChange={(e) => updTexto(i, "simbologia", e.target.value)}>
                      <MenuItem value="code128">Code 128</MenuItem>
                      <MenuItem value="ean13">EAN-13</MenuItem>
                      <MenuItem value="code39">Code 39</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <span style={{ flex: 1 }} />
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); rmTexto(i); }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>
              {t.modo === "fixo" ? (
                <TextField label={(t.tipo || "texto") === "texto" ? "Texto" : "Valor do código"} fullWidth size="small" value={t.texto}
                  onChange={(e) => updTexto(i, "texto", e.target.value)} />
              ) : (
                <Grid container spacing={1}>
                  <Grid item xs={6}><TextField label="Prefixo" size="small" fullWidth value={t.prefixo}
                    onChange={(e) => updTexto(i, "prefixo", e.target.value)} placeholder="Exame: " /></Grid>
                  <Grid item xs={6}><TextField label="Sufixo" size="small" fullWidth value={t.sufixo}
                    onChange={(e) => updTexto(i, "sufixo", e.target.value)} /></Grid>
                  <Grid item xs={3}><TextField label="De" type="number" size="small" fullWidth value={t.inicial}
                    onChange={(e) => updTexto(i, "inicial", Number(e.target.value))} /></Grid>
                  <Grid item xs={3}><TextField label="Até" type="number" size="small" fullWidth value={t.final}
                    onChange={(e) => updTexto(i, "final", Number(e.target.value))} /></Grid>
                  <Grid item xs={3}><TextField label="Passo" type="number" size="small" fullWidth value={t.passo}
                    onChange={(e) => updTexto(i, "passo", Number(e.target.value))} /></Grid>
                  <Grid item xs={3}><TextField label="Zeros" type="number" size="small" fullWidth value={t.zeros}
                    onChange={(e) => updTexto(i, "zeros", Number(e.target.value))} helperText="0=off" /></Grid>
                </Grid>
              )}
              <Grid container spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                <Grid item xs={4}><TextField
                  label={(t.tipo || "texto") === "texto" ? "Tamanho" : (t.tipo === "qrcode" ? "Lado (QR)" : "Altura")}
                  type="number" size="small" fullWidth value={t.tamanhoMm}
                  onChange={(e) => updTexto(i, "tamanhoMm", Number(e.target.value))}
                  InputProps={{ endAdornment: <InputAdornment position="end">mm</InputAdornment> }} /></Grid>
                {(t.tipo || "texto") === "texto" && (
                  <>
                    <Grid item xs={4}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Alinhar</InputLabel>
                        <Select value={t.alinhamento} label="Alinhar" onChange={(e) => updTexto(i, "alinhamento", e.target.value)}>
                          <MenuItem value="left">Esquerda</MenuItem>
                          <MenuItem value="center">Centro</MenuItem>
                          <MenuItem value="right">Direita</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <input type="color" value={t.cor} onChange={(e) => updTexto(i, "cor", e.target.value)}
                        style={{ width: 32, height: 32, border: "none", background: "none" }} />
                      <Typography variant="caption" color="textSecondary">cor</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Fonte</InputLabel>
                        <Select value={t.fonte || "opensans"} label="Fonte" onChange={(e) => updTexto(i, "fonte", e.target.value)}>
                          {FONTES_TEXTO.map((f) => (
                            <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          ))}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => novoTexto("fixo")}>Texto</Button>
            <Button size="small" startIcon={<AddIcon />} onClick={() => novoTexto("sequencial")}>Nº sequencial</Button>
            <Button size="small" startIcon={<AddIcon />} onClick={() => novoTexto("sequencial", "qrcode")}>QR Code</Button>
            <Button size="small" startIcon={<AddIcon />} onClick={() => novoTexto("sequencial", "barcode")}>Cód. barras</Button>
          </div>
          {textos.length > 0 && (
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
              Clique num texto para selecioná-lo e arraste-o no preview grande para posicionar.
            </Typography>
          )}

          {/* Arquivos gerados: baixar PDF/PLT, enviar prova, aprovar */}
          {temArquivos && (
            <>
              <Divider sx={{ my: 2 }}><Typography variant="caption">Arquivos gerados</Typography></Divider>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
                  onClick={() => baixarArquivo("pdf")}>Baixar impressão</Button>
                <Button size="small" variant="outlined" startIcon={<ContentCutIcon />}
                  onClick={() => baixarArquivo("plt")}>Baixar corte (.plt)</Button>
                <Button size="small" variant="outlined" color="secondary" startIcon={<PictureAsPdfIcon />}
                  onClick={() => baixarArquivo("conferencia")}>
                  Conferência (corte na impressão)
                </Button>
                {savedData?.arteAprovada
                  ? <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Arte aprovada" />
                  : <Chip size="small" color="warning" label="Aguardando aprovação" />}
              </div>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                Na <b>conferência</b> a linha magenta é o corte real desenhado sobre a arte — se ela abraça o adesivo, corte e impressão estão casados (desvio no papel é da máquina).
              </Typography>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                {ticketId && (
                  <Button size="small" disabled={enviandoProva} onClick={handleEnviarProva}
                    startIcon={enviandoProva ? <CircularProgress size={14} /> : <SendIcon />}>
                    Enviar prova ao cliente
                  </Button>
                )}
                {!savedData?.arteAprovada && (
                  <Button size="small" color="success" disabled={aprovando} onClick={handleAprovar}
                    startIcon={aprovando ? <CircularProgress size={14} /> : <CheckCircleIcon />}>
                    Cliente aprovou
                  </Button>
                )}
              </div>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                <b>Teste o corte em UMA folha</b> antes da tiragem: leve o .plt no pendrive, imprima o PDF e confira o registro das marcas.
              </Typography>
            </>
          )}
        </Grid>

        {/* Coluna direita: amostra grande (arrastar) em cima + esquema da folha (fixa ao rolar) */}
        <Grid item xs={12} md={7} sx={{ position: { md: "sticky" }, top: 8, alignSelf: "flex-start" }}>
          <Typography variant="caption" color="textSecondary">
            Amostra de um adesivo — arraste a arte para enquadrar
          </Typography>
          {/* Quadro de amostra de 1 adesivo (grande, para ajuste fino) */}
          <div style={{ marginTop: 8, marginBottom: 20 }}>
            <div
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              style={{
                width: adW, height: adH, minWidth: 60,
                border: "1px dashed #bbb", borderRadius: raioAmostra,
                overflow: "hidden", position: "relative",
                cursor: (arteUrl || textos.length) ? "grab" : "default",
                background: "#fafafa", touchAction: "none",
              }}
            >
              {arteUrl && (
                <img src={arteUrl} alt="arte" draggable={false}
                  onLoad={(e) => setArteDim({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                  style={{
                    position: "absolute", left: "50%", top: "50%",
                    transform: `translate(calc(-50% + ${offset.x * mmToPx}px), calc(-50% + ${offset.y * mmToPx}px))`,
                    width: arteEstilo ? arteEstilo.width : "100%",
                    height: arteEstilo ? arteEstilo.height : "auto",
                    objectFit: "fill", transformOrigin: "center",
                  }} />
              )}
              {/* Textos / códigos posicionados (arrastáveis) */}
              {textos.map((t, i) => {
                const tipo = t.tipo || "texto";
                const comum = {
                  position: "absolute", left: "50%", top: "50%",
                  transform: `translate(calc(-50% + ${(t.offsetXMm || 0) * mmToPx}px), calc(-50% + ${(t.offsetYMm || 0) * mmToPx}px))`,
                  cursor: "move",
                  outline: textoSel === i ? "1px dashed #1976d2" : "none",
                };
                if (tipo === "qrcode" || tipo === "barcode") {
                  const lado = Math.max(10, (t.tamanhoMm || 8) * mmToPx);
                  return (
                    <div key={i} data-texto={i} style={{
                      ...comum,
                      width: tipo === "qrcode" ? lado : lado * 2.4, height: lado,
                      background: "repeating-linear-gradient(90deg,#000 0 2px,#fff 2px 4px)",
                      border: "1px solid #444", display: "flex", alignItems: "flex-end",
                      justifyContent: "center", boxSizing: "border-box",
                    }}>
                      <span style={{ fontSize: 8, background: "#fff", padding: "0 2px" }}>
                        {tipo === "qrcode" ? "QR" : "cód"} {textoExemplo(t)}
                      </span>
                    </div>
                  );
                }
                return (
                  <div key={i} data-texto={i} style={{
                    ...comum,
                    fontSize: Math.max(6, (t.tamanhoMm || 4) * mmToPx),
                    color: t.cor || "#000",
                    fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1,
                    textAlign: t.alinhamento || "center", padding: "1px 2px",
                    fontFamily: FONTE_PREVIEW_CSS[t.fonte || "opensans"] || FONTE_PREVIEW_CSS.opensans,
                  }}>
                    {textoExemplo(t)}
                  </div>
                );
              })}
              {/* linha de corte — retângulo/forma padrão, OU o contorno real da silhueta (die-cut) */}
              {form.tipo === "contorno" && contornoPontos ? (
                <svg
                  width={adW} height={adH}
                  // `contornoPontos.pontos` vêm relativos ao CENTRO do bbox
                  // (contrato traceSilhueta/geometria.ts, podem ser negativos)
                  // — o viewBox centralizado faz o enquadramento sem precisar
                  // transladar cada ponto no polygon.
                  viewBox={`${-contornoPontos.larguraMm / 2} ${-contornoPontos.alturaMm / 2} ${contornoPontos.larguraMm} ${contornoPontos.alturaMm}`}
                  preserveAspectRatio="none"
                  style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
                >
                  <polygon
                    points={contornoPontos.pontos.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none" stroke="rgba(0,120,255,.8)" strokeWidth={0.5}
                  />
                </svg>
              ) : (
                <div style={{
                  position: "absolute", inset: 0, borderRadius: raioAmostra,
                  border: "1.5px solid rgba(0,120,255,.6)", pointerEvents: "none",
                }} />
              )}
              {/* furo da tag (indicador) */}
              {form.tipo === "tag" && form.furoMm > 0 && (
                <div style={{
                  position: "absolute", left: "50%",
                  top: (form.furoTopoMm * mmToPx) - (form.furoMm * mmToPx) / 2,
                  transform: "translateX(-50%)",
                  width: form.furoMm * mmToPx, height: form.furoMm * mmToPx,
                  borderRadius: "50%", border: "1.5px solid rgba(0,120,255,.6)",
                  background: "#fff", pointerEvents: "none",
                }} />
              )}
            </div>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
              {arteUrl && "O contorno azul é a linha de corte; a arte além dele é a sangria. "}
              {textos.length > 0 && "Arraste os textos para posicioná-los."}
            </Typography>
          </div>

          {/* Esquema da folha inteira com a grade + cruz de recorte A5 */}
          <Typography variant="caption" color="textSecondary">
            Folha {FOLHA_W}×{FOLHA_H}mm · {calc.porFolha} adesivos
            {form.recorteA5 && <> · linha tracejada = recorte p/ envelope A5</>}
          </Typography>
          <div ref={previewRef} style={{
            position: "relative", width: "100%", aspectRatio: `${FOLHA_W} / ${FOLHA_H}`,
            border: "1px solid #ddd", background: "#fff", marginTop: 4, maxWidth: 230,
          }}>
            {/* 4 marcas em L */}
            {[["0","0"],["r","0"],["0","b"],["r","b"]].map(([hx, hy], i) => (
              <div key={i} style={{
                position: "absolute",
                left: hx === "0" ? "3%" : undefined, right: hx === "r" ? "3%" : undefined,
                top: hy === "0" ? "2%" : undefined, bottom: hy === "b" ? "2%" : undefined,
                width: 10, height: 10,
                borderLeft: hx === "0" ? "2px solid #000" : undefined,
                borderRight: hx === "r" ? "2px solid #000" : undefined,
                borderTop: hy === "0" ? "2px solid #000" : undefined,
                borderBottom: hy === "b" ? "2px solid #000" : undefined,
              }} />
            ))}
            {/* grade aproximada */}
            {calc.colunas > 0 && (
              <div style={{
                position: "absolute", inset: "10% 6%",
                display: "grid",
                gridTemplateColumns: `repeat(${calc.colunas}, 1fr)`,
                gridTemplateRows: `repeat(${calc.linhas}, 1fr)`,
                gap: 1, padding: 2,
              }}>
                {Array.from({ length: Math.min(calc.porFolha, 600) }).map((_, i) => (
                  <div key={i} style={{
                    background: "#ff702133",
                    border: "0.5px solid #ff7021",
                    borderRadius: form.tipo === "redondo" ? "50%" : 1,
                  }} />
                ))}
              </div>
            )}
            {/* Cruz de recorte A5 (2×2) — na posição REAL (folga que evita adesivo) */}
            {calc.porFolha > 0 && form.recorteA5 && calc.recorte && (
              <>
                <div style={{ position: "absolute", left: `${calc.recorte.xPct}%`, top: 0, bottom: 0, borderLeft: "1px dashed #d32f2f" }} />
                <div style={{ position: "absolute", top: `${calc.recorte.yPct}%`, left: 0, right: 0, borderTop: "1px dashed #d32f2f" }} />
              </>
            )}
          </div>
          {form.recorteA5 && calc.recorte && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}
              color={calc.recorte.cabe ? "textSecondary" : "error"}>
              {calc.recorte.cabe
                ? `Recorte em 4 partes (maior: ${calc.recorte.pecaLarguraMm}×${calc.recorte.pecaAlturaMm}mm) — cabe no envelope A5.`
                : `⚠️ Com este tamanho não dá para cortar em 4 sem passar por adesivo E caber no A5 (maior parte ${calc.recorte.pecaLarguraMm}×${calc.recorte.pecaAlturaMm}mm). Ajuste o tamanho/espaço ou corte à mão.`}
            </Typography>
          )}
        </Grid>
      </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button onClick={handleSalvar} disabled={loading}>Salvar rascunho</Button>
        <Button variant="contained" onClick={handleGerar}
          disabled={gerando || calc.porFolha === 0 || (!arteFileName && textos.length === 0)}
          startIcon={gerando ? <CircularProgress size={16} /> : <AutorenewIcon />}>
          {temArquivos ? "Regerar" : `Gerar${calc.folhas ? ` ${calc.folhas} folha(s)` : ""}`}
        </Button>
      </DialogActions>
      <VetorizarDialog
        open={vetorizarOpen}
        onClose={() => setVetorizarOpen(false)}
        arteFileName={arteFileName}
        onAceitar={handleAceitarVetorizacao}
      />

      {/* Salvar como modelo (templates de etiqueta): pede só o nome — a config
          inteira (forma, medidas, textos, fontes) já está salva no adesivo. */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Salvar como modelo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Salva esta configuração (forma, medidas, textos e fontes) como um modelo
            reutilizável — útil para pedidos recorrentes, como etiqueta de laboratório/farmácia.
          </Typography>
          <TextField autoFocus fullWidth size="small" label="Nome do modelo"
            value={templateNome} onChange={(e) => setTemplateNome(e.target.value)}
            inputProps={{ maxLength: 60 }}
            placeholder="Ex.: Etiqueta laboratório 30mm"
            onKeyDown={(e) => { if (e.key === "Enter") handleSalvarTemplate(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={salvandoTemplate || !templateNome.trim()}
            startIcon={salvandoTemplate ? <CircularProgress size={16} /> : <BookmarkIcon />}
            onClick={handleSalvarTemplate}>
            Salvar modelo
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AdesivoDialog;
